import React, { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../../utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      leftIcon,
      rightIcon,
      fullWidth = false,
      id,
      type,
      onWheel,
      ...props
    },
    ref
  ) => {
    const inputId = id || React.useId();
    
    // Prevent mouse wheel from changing number inputs
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      if (type === 'number') {
        // Blur the input to prevent scroll change
        e.currentTarget.blur();
        // Call custom onWheel handler if provided
        if (onWheel) {
          onWheel(e);
        }
      }
    };

    // Block negative numbers globally on number inputs for physical dimensions
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (type === 'number' && e.key === '-') {
        e.preventDefault();
      }
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };
    
    return (
      <div className={cn('space-y-1', fullWidth ? 'w-full' : 'w-fit', className)}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            type={type}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
            className={cn(
              'block w-full px-3 py-2 bg-white border-2',
              'rounded-lg text-gray-900 text-base font-semibold placeholder-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-all duration-200',
              'min-h-[38px]',
              error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-orange-200 hover:border-orange-300',
              leftIcon ? 'pl-10' : 'pl-3',
              rightIcon ? 'pr-10' : 'pr-3',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-500">{error}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
